import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericField, OId } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { useForm } from '@FormModule';
import { getDefaultDateFormat } from '@Helpers';
import { useAnimalProfileStore } from '@Stores';

interface MedicalStaff {
  [key: string]: any;
  first_name: string;
  last_name: string;
  _id: OId;
}

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();

export default function () {
  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { medicalStaff } = storeToRefs(useAnimalProfileStore());
  const { t } = useI18n();

  const rawFields: RawField[] = [
    {
      label: 'Follow-Up Date',
      name: 'date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Follow-Up By',
      name: 'by',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Other Veterinarian',
      name: 'other_by',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Follow-Up Reason',
      name: 'reason',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Follow-Up Notes',
      name: 'notes',
      required: false,
      type: 'textarea',
      value: '',
    },
  ];

  const attendingVetOptions = computed(() =>
    medicalStaff.value.map((staff: MedicalStaff) => {
      const { first_name, last_name } = staff;
      return {
        ...staff,
        by: `${first_name} ${last_name}`,
      };
    })
  );

  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      by: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
      },
    })
  );

  const byField = computed(() => fieldsSchema.value.find(({ name }) => name === 'by'));
  const byModel = computed(() => byField.value?.model);

  watch(byModel, () => showOtherVetField());

  function showOtherVetField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_by') {
        const showField = byModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (byModel?.value?.by !== t('common.other')) {
          field.model = '';
          field.value = '';
          field.state.errors.clear();
        }
      }
    });

    return true;
  }

  function setForm(sourceData?: any) {
    const updatedFieldSchema = getDetailsSchema(rawFields, fieldsSettings.value);
    if (!sourceData) return (fieldsSchema.value = updatedFieldSchema);

    formError.value = null;
    wasFormSubmitted.value = false;

    updatedFieldSchema.forEach((field) => {
      const match = sourceData[field.name];

      // prettier-ignore
      switch (field.name) {
        case 'date': return setDateField(field, match)
        case 'time_until_followup': return setFollowUpField(field, sourceData)
        case 'by': return setByField(field, sourceData)
        case 'other_by': return setOtherByField(field, sourceData)
        default: setMatchField(field, match);
      }
    });

    fieldsSchema.value = updatedFieldSchema;
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
  }

  function setFollowUpField(field: any, sourceData: any) {
    const timeFrame = {
      duration: sourceData.time_until_followup,
      duration_unit: sourceData.followup_unit,
    };

    field.model = timeFrame;
    field.value = timeFrame;
  }

  function setByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    if (sourceData.by_id === 'other') {
      field.model = { by: 'Other' };
      field.value = { by: 'Other' };

      return;
    }

    const fullStaffData = medicalStaff.value.find((staff: MedicalStaff) => staff._id?.$oid === sourceData.by_id?.$oid);
    if (!fullStaffData) return;

    const { first_name, last_name, _id } = fullStaffData;
    const by = { _id, first_name, last_name, by: sourceData.by };

    field.model = by;
    field.value = by;
  }

  function setOtherByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    field.model = sourceData.by;
    field.value = sourceData.by;
  }

  function setDateField(field: any, date: any) {
    field.model = getDefaultDateFormat(date) || new Date();
    field.value = getDefaultDateFormat(date) || new Date();
  }

  function resetForm() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    formError,
    rawFields,

    resetForm,
    setForm,
  };
}
